import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  FaPlus,
  FaTrash,
  FaUserShield,
  FaSearch,
  FaChevronDown,
  FaUsers,
  FaUser,
  FaCheck,
  FaLock,
  FaLayerGroup,
} from 'react-icons/fa';
import DiscordUserChip from './DiscordUserChip';
import './PermissionsPanel.css';

const emptyRoleGrant = () => ({
  roleId: '',
  preset: 'support',
  tabs: [],
  tabActions: {},
  capabilities: [],
});

const emptyUserGrant = () => ({
  userId: '',
  preset: 'support',
  tabs: [],
  tabActions: {},
  capabilities: [],
});

function actionsForTab(tabDef) {
  return Array.isArray(tabDef?.actions) ? tabDef.actions : [];
}

function defaultActions(tabDef) {
  return actionsForTab(tabDef).map((a) => a.id);
}

function grantSummary(grant, tabDefs) {
  const tabs = grant.tabs || [];
  const caps = Array.isArray(grant.capabilities) ? grant.capabilities : [];
  const capNote =
    caps.includes('bypass_coming_soon') ? ' · Acesso antecipado' : '';
  if (!tabs.length) {
    return caps.length
      ? `Nenhuma área liberada${capNote}`
      : 'Nenhuma área liberada';
  }
  const parts = tabs.slice(0, 4).map((id) => {
    const def = tabDefs.find((t) => t.id === id);
    const label = def?.label || id;
    const acts = grant.tabActions?.[id];
    const all = defaultActions(def);
    if (!all.length) return label;
    const selected = Array.isArray(acts) && acts.length ? acts : all;
    if (selected.length >= all.length) return label;
    if (selected.length === 1 && selected[0] === 'view') return `${label} (leitura)`;
    return `${label} (${selected.length}/${all.length})`;
  });
  const extra = tabs.length > 4 ? ` +${tabs.length - 4}` : '';
  return parts.join(' · ') + extra + capNote;
}

function accessLevel(grant, tabDefs) {
  const tabs = grant.tabs || [];
  if (!tabs.length) return { id: 'none', label: 'Vazio' };
  let full = 0;
  let partial = 0;
  for (const id of tabs) {
    const def = tabDefs.find((t) => t.id === id);
    const all = defaultActions(def);
    if (!all.length) {
      full += 1;
      continue;
    }
    const selected = new Set(
      grant.tabActions?.[id]?.length ? grant.tabActions[id] : all
    );
    if (all.every((a) => selected.has(a))) full += 1;
    else partial += 1;
  }
  if (partial === 0 && full === tabs.length) {
    return { id: 'full', label: 'Completo' };
  }
  if (full === 0 && partial > 0) {
    return { id: 'limited', label: 'Restrito' };
  }
  return { id: 'mixed', label: 'Misto' };
}

/**
 * Controle de acesso do painel — layout master-detail profissional.
 */
const PermissionsPanel = ({
  settings,
  catalog,
  guildRoles = [],
  guildMembers = [],
  guildMembersLoading = false,
  guildMembersError = '',
  guildResourcesLoading = false,
  saving = false,
  message = '',
  onChange,
  onSave,
}) => {
  const [scope, setScope] = useState('roles'); // roles | users
  const [selectedKey, setSelectedKey] = useState(null);
  const [listQuery, setListQuery] = useState('');
  const [rolePick, setRolePick] = useState('');
  const [userQuery, setUserQuery] = useState('');
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userPickerRef = useRef(null);
  const userInputRef = useRef(null);

  const presets = catalog?.presets || [];
  const tabDefs = catalog?.tabs || [];
  const capabilityDefs = catalog?.capabilities || [];
  const roleGrants = Array.isArray(settings?.roleGrants) ? settings.roleGrants : [];
  const userGrants = Array.isArray(settings?.userGrants) ? settings.userGrants : [];

  const roleNameById = useMemo(() => {
    const map = new Map();
    for (const r of guildRoles) map.set(String(r.id), r.name);
    return map;
  }, [guildRoles]);

  const usedRoleIds = useMemo(
    () => new Set(roleGrants.map((g) => String(g.roleId))),
    [roleGrants]
  );

  const usedUserIds = useMemo(
    () => new Set(userGrants.map((g) => String(g.userId))),
    [userGrants]
  );

  const availableRoles = useMemo(
    () =>
      guildRoles
        .filter((r) => !usedRoleIds.has(String(r.id)))
        .sort((a, b) => (b.position || 0) - (a.position || 0)),
    [guildRoles, usedRoleIds]
  );

  const filteredMembers = useMemo(() => {
    const q = userQuery.trim().toLowerCase();
    return guildMembers
      .filter((m) => !usedUserIds.has(String(m.id)))
      .filter((m) => {
        if (!q) return true;
        const hay = [m.displayName, m.globalName, m.username, m.nick, m.id]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        return hay.includes(q);
      })
      .slice(0, 80);
  }, [guildMembers, usedUserIds, userQuery]);

  const rawIdDraft = String(userQuery || '').trim();
  const canAddRawId =
    /^\d{5,32}$/.test(rawIdDraft) && !usedUserIds.has(rawIdDraft);

  const roleItems = useMemo(() => {
    const q = listQuery.trim().toLowerCase();
    return roleGrants
      .map((g, index) => ({
        key: `role:${g.roleId}`,
        kind: 'role',
        index,
        grant: g,
        title: roleNameById.get(String(g.roleId)) || `Cargo ${g.roleId}`,
        subtitle: g.roleId,
      }))
      .filter((item) => {
        if (!q) return true;
        return (
          item.title.toLowerCase().includes(q) ||
          String(item.grant.roleId).includes(q)
        );
      });
  }, [roleGrants, roleNameById, listQuery]);

  const userItems = useMemo(() => {
    const q = listQuery.trim().toLowerCase();
    return userGrants
      .map((g, index) => ({
        key: `user:${g.userId}`,
        kind: 'user',
        index,
        grant: g,
        title:
          g.user?.displayName ||
          g.user?.globalName ||
          g.user?.username ||
          `Usuário ${g.userId}`,
        subtitle: g.userId,
      }))
      .filter((item) => {
        if (!q) return true;
        const hay = [
          item.title,
          item.subtitle,
          item.grant.user?.username,
          item.grant.user?.globalName,
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        return hay.includes(q);
      });
  }, [userGrants, listQuery]);

  const items = scope === 'roles' ? roleItems : userItems;

  const selected = useMemo(() => {
    if (!selectedKey) return null;
    return items.find((i) => i.key === selectedKey) || null;
  }, [items, selectedKey]);

  useEffect(() => {
    if (selectedKey && !items.some((i) => i.key === selectedKey)) {
      setSelectedKey(items[0]?.key || null);
    }
  }, [items, selectedKey]);

  useEffect(() => {
    if (!userMenuOpen) return undefined;
    const onDoc = (e) => {
      if (userPickerRef.current && !userPickerRef.current.contains(e.target)) {
        setUserMenuOpen(false);
      }
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setUserMenuOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [userMenuOpen]);

  const presetTabs = (presetId) => {
    const p = presets.find((x) => x.id === presetId);
    return p?.tabs ? [...p.tabs] : [];
  };

  const presetTabActions = (presetId) => {
    const p = presets.find((x) => x.id === presetId);
    return p?.tabActions ? { ...p.tabActions } : {};
  };

  const presetCapabilities = (presetId) => {
    const p = presets.find((x) => x.id === presetId);
    return Array.isArray(p?.capabilities) ? [...p.capabilities] : [];
  };

  const updateGrant = (kind, index, patch) => {
    if (kind === 'role') {
      const next = roleGrants.map((g, i) => {
        if (i !== index) return g;
        const merged = { ...g, ...patch };
        if (patch.preset && patch.preset !== 'custom') {
          merged.tabs = presetTabs(patch.preset);
          merged.tabActions = presetTabActions(patch.preset);
          merged.capabilities = presetCapabilities(patch.preset);
        }
        return merged;
      });
      onChange({ ...settings, roleGrants: next });
    } else {
      const next = userGrants.map((g, i) => {
        if (i !== index) return g;
        const merged = { ...g, ...patch };
        if (patch.preset && patch.preset !== 'custom') {
          merged.tabs = presetTabs(patch.preset);
          merged.tabActions = presetTabActions(patch.preset);
          merged.capabilities = presetCapabilities(patch.preset);
        }
        return merged;
      });
      onChange({ ...settings, userGrants: next });
    }
  };

  const applyCustomTabs = (kind, index, tabs) => {
    const list = kind === 'role' ? roleGrants : userGrants;
    const grant = list[index];
    if (!grant) return;
    const prevActions = grant.tabActions || {};
    const tabActions = {};
    for (const tabDef of tabDefs) {
      if (!tabs.includes(tabDef.id)) continue;
      const actionDefs = actionsForTab(tabDef);
      if (!actionDefs.length) continue;
      if (Array.isArray(prevActions[tabDef.id]) && prevActions[tabDef.id].length) {
        const allowed = new Set(actionDefs.map((a) => a.id));
        const kept = prevActions[tabDef.id].filter(
          (a) => allowed.has(a) || a === 'scoped'
        );
        const withView = kept.includes('view')
          ? kept
          : ['view', ...kept.filter((a) => a !== 'view')];
        const needsScoped =
          (tabDef.id === 'support_chat' || tabDef.id === 'marketing') &&
          !withView.includes('scoped');
        tabActions[tabDef.id] = needsScoped
          ? [...withView.filter((a) => a !== 'scoped'), 'scoped']
          : withView;
      } else {
        const defaults = defaultActions(tabDef);
        tabActions[tabDef.id] =
          tabDef.id === 'support_chat' || tabDef.id === 'marketing'
            ? [...defaults, 'scoped']
            : defaults;
      }
    }
    updateGrant(kind, index, { preset: 'custom', tabs, tabActions });
  };

  const toggleTab = (kind, index, tabId) => {
    const list = kind === 'role' ? roleGrants : userGrants;
    const grant = list[index];
    if (!grant) return;
    const current = new Set(grant.tabs || []);
    if (current.has(tabId)) current.delete(tabId);
    else current.add(tabId);
    const tabs = tabDefs.map((t) => t.id).filter((id) => current.has(id));
    applyCustomTabs(kind, index, tabs);
  };

  const setAllTabs = (kind, index, enabled) => {
    if (!enabled) {
      applyCustomTabs(kind, index, []);
      return;
    }
    applyCustomTabs(
      kind,
      index,
      tabDefs.map((t) => t.id)
    );
  };

  const toggleTabAction = (kind, index, tabId, actionId) => {
    if (actionId === 'view') return;
    const list = kind === 'role' ? roleGrants : userGrants;
    const grant = list[index];
    if (!grant || !(grant.tabs || []).includes(tabId)) return;
    const tabDef = tabDefs.find((t) => t.id === tabId);
    const allowed = defaultActions(tabDef);
    if (!allowed.includes(actionId)) return;
    const current = new Set(
      (grant.tabActions?.[tabId]?.length
        ? grant.tabActions[tabId]
        : allowed
      ).filter((a) => a !== 'scoped')
    );
    if (current.has(actionId)) current.delete(actionId);
    else current.add(actionId);
    current.add('view');
    const nextActions = allowed.filter((a) => current.has(a));
    // Marker: matriz granular configurada (evita remigração de extras)
    if (tabId === 'support_chat' || tabId === 'marketing') {
      nextActions.push('scoped');
    }
    updateGrant(kind, index, {
      preset: 'custom',
      tabActions: {
        ...(grant.tabActions || {}),
        [tabId]: nextActions,
      },
    });
  };

  const toggleCapability = (kind, index, capabilityId) => {
    const list = kind === 'role' ? roleGrants : userGrants;
    const grant = list[index];
    if (!grant) return;
    const allowed = new Set(capabilityDefs.map((c) => c.id));
    if (!allowed.has(capabilityId)) return;
    const current = new Set(
      Array.isArray(grant.capabilities) ? grant.capabilities : []
    );
    if (current.has(capabilityId)) current.delete(capabilityId);
    else current.add(capabilityId);
    updateGrant(kind, index, {
      preset: 'custom',
      capabilities: capabilityDefs
        .map((c) => c.id)
        .filter((id) => current.has(id)),
    });
  };

  const addRoleGrant = () => {
    if (!rolePick) return;
    const grant = {
      ...emptyRoleGrant(),
      roleId: rolePick,
      tabs: presetTabs('support'),
      tabActions: presetTabActions('support'),
      capabilities: presetCapabilities('support'),
    };
    onChange({ ...settings, roleGrants: [...roleGrants, grant] });
    setSelectedKey(`role:${rolePick}`);
    setRolePick('');
    setScope('roles');
  };

  const removeSelected = () => {
    if (!selected) return;
    if (selected.kind === 'role') {
      onChange({
        ...settings,
        roleGrants: roleGrants.filter((_, i) => i !== selected.index),
      });
    } else {
      onChange({
        ...settings,
        userGrants: userGrants.filter((_, i) => i !== selected.index),
      });
    }
    setSelectedKey(null);
  };

  const addUserGrant = (memberOrId) => {
    const isMember = memberOrId && typeof memberOrId === 'object';
    const id = String(isMember ? memberOrId.id : memberOrId || '').trim();
    if (!/^\d{5,32}$/.test(id)) return;
    if (usedUserIds.has(id)) {
      setUserQuery('');
      setUserMenuOpen(false);
      return;
    }
    const grant = {
      ...emptyUserGrant(),
      userId: id,
      tabs: presetTabs('support'),
      tabActions: presetTabActions('support'),
      capabilities: presetCapabilities('support'),
      ...(isMember ? { user: memberOrId } : {}),
    };
    onChange({ ...settings, userGrants: [...userGrants, grant] });
    setUserQuery('');
    setUserMenuOpen(false);
    setScope('users');
    setSelectedKey(`user:${id}`);
  };

  if (!settings) return null;

  const grant = selected?.grant;
  const level = grant ? accessLevel(grant, tabDefs) : null;
  const isCustom = (grant?.preset || 'custom') === 'custom';

  return (
    <section className="admin-section perm-panel">
      <header className="perm-hero">
        <div className="perm-hero__copy">
          <p className="perm-hero__kicker">
            <FaUserShield aria-hidden="true" /> Controle de acesso
          </p>
          <h1>Permissões do painel</h1>
          <p>
            Defina quem entra em cada área e o que pode fazer — ver, gerenciar
            ou excluir. A união de cargos e grants individuais forma o acesso
            final do membro.
          </p>
        </div>
        <div className="perm-hero__stats">
          <div className="perm-stat">
            <span className="perm-stat__value">{roleGrants.length}</span>
            <span className="perm-stat__label">Cargos</span>
          </div>
          <div className="perm-stat">
            <span className="perm-stat__value">{userGrants.length}</span>
            <span className="perm-stat__label">Usuários</span>
          </div>
          <div className="perm-stat">
            <span className="perm-stat__value">{tabDefs.length}</span>
            <span className="perm-stat__label">Áreas</span>
          </div>
        </div>
      </header>

      {message && <p className="admin-success">{message}</p>}

      <div className="perm-shell">
        <aside className="perm-sidebar">
          <div className="perm-scope" role="tablist" aria-label="Tipo de grant">
            <button
              type="button"
              role="tab"
              aria-selected={scope === 'roles'}
              className={`perm-scope__btn${scope === 'roles' ? ' is-active' : ''}`}
              onClick={() => {
                setScope('roles');
                setListQuery('');
                setSelectedKey(null);
              }}
            >
              <FaUsers aria-hidden="true" /> Cargos
              <span>{roleGrants.length}</span>
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={scope === 'users'}
              className={`perm-scope__btn${scope === 'users' ? ' is-active' : ''}`}
              onClick={() => {
                setScope('users');
                setListQuery('');
                setSelectedKey(null);
              }}
            >
              <FaUser aria-hidden="true" /> Usuários
              <span>{userGrants.length}</span>
            </button>
          </div>

          <div className="perm-sidebar__search">
            <FaSearch aria-hidden="true" />
            <input
              type="search"
              value={listQuery}
              onChange={(e) => setListQuery(e.target.value)}
              placeholder={
                scope === 'roles' ? 'Filtrar cargos…' : 'Filtrar usuários…'
              }
            />
          </div>

          {scope === 'roles' ? (
            <div className="perm-add-row">
              <select
                value={rolePick}
                onChange={(e) => setRolePick(e.target.value)}
                disabled={guildResourcesLoading || !availableRoles.length}
              >
                <option value="">
                  {guildResourcesLoading
                    ? 'Carregando cargos…'
                    : availableRoles.length
                      ? 'Adicionar cargo…'
                      : 'Nenhum cargo disponível'}
                </option>
                {availableRoles.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.name}
                  </option>
                ))}
              </select>
              <button
                type="button"
                className="admin-btn admin-btn--primary admin-btn--sm"
                onClick={addRoleGrant}
                disabled={!rolePick}
                aria-label="Adicionar cargo"
              >
                <FaPlus aria-hidden="true" />
              </button>
            </div>
          ) : (
            <div className="perm-member-picker" ref={userPickerRef}>
              <div
                className={`perm-member-picker__control${guildMembersLoading ? ' is-loading' : ''}`}
                onClick={() => {
                  if (guildMembersLoading) return;
                  setUserMenuOpen(true);
                  setTimeout(() => userInputRef.current?.focus(), 0);
                }}
              >
                <FaSearch aria-hidden="true" />
                <input
                  ref={userInputRef}
                  type="text"
                  value={userQuery}
                  disabled={guildMembersLoading}
                  placeholder={
                    guildMembersLoading
                      ? 'Carregando membros…'
                      : 'Buscar membro para adicionar…'
                  }
                  onChange={(e) => {
                    setUserQuery(e.target.value);
                    setUserMenuOpen(true);
                  }}
                  onFocus={() => setUserMenuOpen(true)}
                  onClick={(e) => e.stopPropagation()}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && canAddRawId) {
                      e.preventDefault();
                      addUserGrant(rawIdDraft);
                    }
                  }}
                />
                <FaChevronDown aria-hidden="true" />
              </div>
              {userMenuOpen && !guildMembersLoading && (
                <div className="perm-member-picker__menu" role="listbox">
                  {guildMembersError && guildMembers.length === 0 ? (
                    <p className="perm-member-picker__empty">
                      {guildMembersError}
                      {canAddRawId
                        ? ' Cole o ID e pressione Enter.'
                        : ''}
                    </p>
                  ) : filteredMembers.length === 0 ? (
                    <p className="perm-member-picker__empty">
                      {canAddRawId
                        ? 'Nenhum resultado — Enter para adicionar este ID.'
                        : userQuery.trim()
                          ? 'Nenhum membro encontrado.'
                          : 'Todos os membros já foram adicionados.'}
                    </p>
                  ) : (
                    filteredMembers.map((m) => (
                      <button
                        key={m.id}
                        type="button"
                        className="perm-member-picker__option"
                        role="option"
                        onClick={() => addUserGrant(m)}
                      >
                        <DiscordUserChip user={m} userId={m.id} compact />
                        {m.username && m.displayName !== m.username ? (
                          <span className="perm-member-picker__username">
                            @{m.username}
                          </span>
                        ) : null}
                      </button>
                    ))
                  )}
                </div>
              )}
              {canAddRawId && (
                <button
                  type="button"
                  className="perm-add-raw"
                  onClick={() => addUserGrant(rawIdDraft)}
                >
                  <FaPlus aria-hidden="true" /> Adicionar ID {rawIdDraft}
                </button>
              )}
            </div>
          )}

          <ul className="perm-nav" aria-label="Grants">
            {items.length === 0 ? (
              <li className="perm-nav__empty">
                {scope === 'roles'
                  ? 'Nenhum cargo liberado ainda.'
                  : 'Nenhum usuário individual ainda.'}
              </li>
            ) : (
              items.map((item) => {
                const lvl = accessLevel(item.grant, tabDefs);
                const presetLabel =
                  presets.find((p) => p.id === (item.grant.preset || 'custom'))
                    ?.label || 'Personalizado';
                return (
                  <li key={item.key}>
                    <button
                      type="button"
                      className={`perm-nav__item${selectedKey === item.key ? ' is-active' : ''}`}
                      onClick={() => setSelectedKey(item.key)}
                    >
                      <span className="perm-nav__title">
                        {item.kind === 'user' ? (
                          <DiscordUserChip
                            user={item.grant.user}
                            userId={item.grant.userId}
                            compact
                          />
                        ) : (
                          <strong>{item.title}</strong>
                        )}
                      </span>
                      <span className="perm-nav__meta">
                        <span className={`perm-pill perm-pill--${lvl.id}`}>
                          {lvl.label}
                        </span>
                        <span className="perm-nav__preset">{presetLabel}</span>
                      </span>
                      <span className="perm-nav__summary">
                        {grantSummary(item.grant, tabDefs)}
                      </span>
                    </button>
                  </li>
                );
              })
            )}
          </ul>
        </aside>

        <div className="perm-main">
          {!selected || !grant ? (
            <div className="perm-empty">
              <FaLayerGroup aria-hidden="true" />
              <h2>Selecione um grant</h2>
              <p>
                Escolha um cargo ou usuário à esquerda para editar o preset e a
                matriz de permissões. Ou adicione um novo no topo da lista.
              </p>
            </div>
          ) : (
            <>
              <header className="perm-editor__head">
                <div className="perm-editor__identity">
                  {selected.kind === 'user' ? (
                    <DiscordUserChip
                      user={grant.user}
                      userId={grant.userId}
                    />
                  ) : (
                    <div>
                      <h2>{selected.title}</h2>
                      <p className="perm-mono">{grant.roleId}</p>
                    </div>
                  )}
                  {level && (
                    <span className={`perm-pill perm-pill--${level.id}`}>
                      {level.label}
                    </span>
                  )}
                </div>
                <button
                  type="button"
                  className="admin-btn admin-btn--danger admin-btn--sm"
                  onClick={removeSelected}
                >
                  <FaTrash aria-hidden="true" /> Remover
                </button>
              </header>

              <div className="perm-presets">
                <p className="perm-presets__label">Modelo de acesso</p>
                <div className="perm-presets__grid" role="radiogroup">
                  {presets.map((p) => {
                    const active = (grant.preset || 'custom') === p.id;
                    return (
                      <button
                        key={p.id}
                        type="button"
                        role="radio"
                        aria-checked={active}
                        className={`perm-preset${active ? ' is-active' : ''}`}
                        onClick={() =>
                          updateGrant(selected.kind, selected.index, {
                            preset: p.id,
                          })
                        }
                      >
                        <span className="perm-preset__check" aria-hidden="true">
                          {active ? <FaCheck /> : null}
                        </span>
                        <strong>{p.label}</strong>
                        <span>{p.description}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {capabilityDefs.length > 0 && (
                <div className="perm-capabilities">
                  <div className="perm-capabilities__head">
                    <h3>Capacidades do site</h3>
                    <p>
                      {isCustom
                        ? 'Permissões especiais fora das abas do painel.'
                        : 'Definidas pelo preset — mude para Personalizado para editar.'}
                    </p>
                  </div>
                  <div
                    className="perm-capabilities__list"
                    role="group"
                    aria-label="Capacidades do site"
                  >
                    {capabilityDefs.map((cap) => {
                      const checked = (
                        Array.isArray(grant.capabilities)
                          ? grant.capabilities
                          : []
                      ).includes(cap.id);
                      const locked = !isCustom;
                      return (
                        <label
                          key={cap.id}
                          className={`perm-capability${checked ? ' is-on' : ''}${locked ? ' is-locked' : ''}`}
                          title={cap.description || ''}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            disabled={locked}
                            onChange={() =>
                              toggleCapability(
                                selected.kind,
                                selected.index,
                                cap.id
                              )
                            }
                          />
                          <span className="perm-capability__box" aria-hidden="true">
                            {checked ? (
                              locked ? (
                                <FaLock />
                              ) : (
                                <FaCheck />
                              )
                            ) : null}
                          </span>
                          <span className="perm-capability__copy">
                            <strong>{cap.label}</strong>
                            {cap.description ? <small>{cap.description}</small> : null}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}

              <div className="perm-matrix-wrap">
                <div className="perm-matrix__toolbar">
                  <div>
                    <h3>Matriz de acesso</h3>
                    <p>
                      {isCustom
                        ? 'Marque áreas e refine Ver / Gerenciar / Excluir. No Chat VIP e Marketing, use as ações extras por capacidade.'
                        : 'Preset ativo — mude para Personalizado para editar a matriz.'}
                    </p>
                  </div>
                  {isCustom && (
                    <div className="perm-matrix__quick">
                      <button
                        type="button"
                        className="admin-btn admin-btn--ghost admin-btn--sm"
                        onClick={() =>
                          setAllTabs(selected.kind, selected.index, true)
                        }
                      >
                        Liberar tudo
                      </button>
                      <button
                        type="button"
                        className="admin-btn admin-btn--ghost admin-btn--sm"
                        onClick={() =>
                          setAllTabs(selected.kind, selected.index, false)
                        }
                      >
                        Limpar
                      </button>
                    </div>
                  )}
                </div>

                <div className="perm-matrix-scroll">
                  <table className="perm-matrix">
                    <thead>
                      <tr>
                        <th>Área</th>
                        <th>Acesso</th>
                        <th>Ver</th>
                        <th>Gerenciar</th>
                        <th>Excluir</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tabDefs.map((t) => {
                        const enabled = (grant.tabs || []).includes(t.id);
                        const actionDefs = actionsForTab(t);
                        const selectedActions = new Set(
                          (grant.tabActions?.[t.id]?.length
                            ? grant.tabActions[t.id]
                            : defaultActions(t)
                          ).filter((a) => a !== 'scoped')
                        );
                        const has = (id) =>
                          actionDefs.some((a) => a.id === id);
                        const coreIds = ['view', 'edit', 'delete'];
                        const extraDefs = actionDefs.filter(
                          (a) => !coreIds.includes(a.id)
                        );
                        return (
                          <React.Fragment key={t.id}>
                            <tr className={enabled ? 'is-on' : 'is-off'}>
                              <td>
                                <div className="perm-matrix__area">
                                  <strong>{t.label}</strong>
                                  {actionDefs.length === 1 &&
                                  actionDefs[0].id === 'view' ? (
                                    <small>Somente leitura</small>
                                  ) : extraDefs.length > 0 ? (
                                    <small>Ações extras abaixo</small>
                                  ) : null}
                                </div>
                              </td>
                              <td>
                                <label className="perm-switch">
                                  <input
                                    type="checkbox"
                                    checked={enabled}
                                    disabled={!isCustom}
                                    onChange={() =>
                                      toggleTab(
                                        selected.kind,
                                        selected.index,
                                        t.id
                                      )
                                    }
                                  />
                                  <span />
                                </label>
                              </td>
                              {coreIds.map((actionId) => {
                                if (!has(actionId)) {
                                  return (
                                    <td
                                      key={actionId}
                                      className="perm-matrix__na"
                                    >
                                      <span
                                        className="perm-na"
                                        aria-hidden="true"
                                      >
                                        —
                                      </span>
                                    </td>
                                  );
                                }
                                const locked =
                                  !isCustom || !enabled || actionId === 'view';
                                const checked =
                                  enabled && selectedActions.has(actionId);
                                const isViewLock =
                                  actionId === 'view' && enabled;
                                return (
                                  <td key={actionId}>
                                    <label
                                      className={`perm-check${checked ? ' is-on' : ''}${locked ? ' is-locked' : ''}${isViewLock ? ' is-required' : ''}`}
                                      title={
                                        actionDefs.find(
                                          (a) => a.id === actionId
                                        )?.description || actionId
                                      }
                                    >
                                      <input
                                        type="checkbox"
                                        checked={checked}
                                        disabled={locked}
                                        onChange={() =>
                                          toggleTabAction(
                                            selected.kind,
                                            selected.index,
                                            t.id,
                                            actionId
                                          )
                                        }
                                      />
                                      <span
                                        className="perm-check__box"
                                        aria-hidden="true"
                                      >
                                        {checked ? (
                                          isViewLock ? (
                                            <FaLock />
                                          ) : (
                                            <FaCheck />
                                          )
                                        ) : null}
                                      </span>
                                    </label>
                                  </td>
                                );
                              })}
                            </tr>
                            {extraDefs.length > 0 && (
                              <tr
                                className={`perm-matrix__extras${enabled ? ' is-on' : ' is-off'}`}
                              >
                                <td colSpan={5}>
                                  <div
                                    className="perm-extra-actions"
                                    role="group"
                                    aria-label={`Ações de ${t.label}`}
                                  >
                                    {extraDefs.map((actionDef) => {
                                      const locked = !isCustom || !enabled;
                                      const checked =
                                        enabled &&
                                        selectedActions.has(actionDef.id);
                                      return (
                                        <label
                                          key={actionDef.id}
                                          className={`perm-extra-chip${checked ? ' is-on' : ''}${locked ? ' is-locked' : ''}`}
                                          title={actionDef.description || ''}
                                        >
                                          <input
                                            type="checkbox"
                                            checked={checked}
                                            disabled={locked}
                                            onChange={() =>
                                              toggleTabAction(
                                                selected.kind,
                                                selected.index,
                                                t.id,
                                                actionDef.id
                                              )
                                            }
                                          />
                                          <span className="perm-extra-chip__box">
                                            {checked ? (
                                              <FaCheck />
                                            ) : locked && enabled ? (
                                              <FaLock />
                                            ) : null}
                                          </span>
                                          <span className="perm-extra-chip__label">
                                            {actionDef.label}
                                          </span>
                                        </label>
                                      );
                                    })}
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}

          <footer className="perm-footer">
            <p>
              Alterações só valem depois de salvar. O dono do bot sempre tem
              acesso total, inclusive a esta aba.
            </p>
            <button
              type="button"
              className="admin-btn admin-btn--primary"
              onClick={onSave}
              disabled={saving}
            >
              {saving ? 'Salvando…' : 'Salvar permissões'}
            </button>
          </footer>
        </div>
      </div>
    </section>
  );
};

export default PermissionsPanel;
